import { useEffect } from 'react';
import moment from 'moment';
import { DatePicker as AntdDatePicker } from 'antd';
import { useAwaitControl } from 'react-redux-await-control';
import { getTicketsByUser } from '~/store/tickets/actions';
import { Ticket } from '~/typings/entities/Ticket';
import { Button, Divider, Flex, Input, Select, Typography } from '~/ui/components';

export type StepTicketProps = {
  personId?: number;
  contractId?: string;
  ticketId?: string;
  conversationId: string;
  dueDate?: string;
  loading?: boolean;
  onTicketChange: (ticketId: string) => void;
  onConversationChange: (conversationId: string) => void;
  onDueDateChange: (dueDate: string) => void;
  onFinalize: () => void;
};

export function StepTicket({
  personId,
  contractId,
  ticketId,
  conversationId,
  dueDate,
  loading,
  onTicketChange,
  onConversationChange,
  onDueDateChange,
  onFinalize,
}: StepTicketProps) {
  const ticketsControl = useAwaitControl(getTicketsByUser);
  let rawTickets: Ticket[] = ticketsControl.result();
  const isLoading = ticketsControl.isRunning();
  const hasLoaded = ticketsControl.isSuccessful();

  useEffect(() => {
    if (personId && contractId) {
      ticketsControl.start({ personId, contractId });
    }
  }, [personId, contractId]);

  rawTickets = rawTickets
    .filter((item) => item.statusTicket === 'Aguardando tratativa')
    .filter((item) => item.contracts.filter((contract) => contract.id === parseInt(contractId)).length > 0)
    .filter((item) => item.category?.description === 'Cobrança' || item.journey == 'Cobrança');

  ticketId = ticketId || (rawTickets.length > 0 ? String(rawTickets[0].id) : undefined);

  const ticketOptions = (Array.isArray(rawTickets) ? rawTickets : []).map((t) => ({
    value: String(t.id),
    label: String(t.id),
  }));

  ticketId = ticketId || (ticketOptions[0]?.value ?? '');

  const canFinalize = (!!ticketId || !!conversationId) && !!dueDate;

  return (
    <Flex direction="column" style={{ height: 'calc(100vh - 84px)' }}>
      <Flex direction="column" gap={16} style={{ flex: 1, overflow: 'auto', paddingTop: 16 }}>
        <Typography type="bodySmall" color="element.secondary" style={{ marginTop: 16 }}>
          Para gerar o boleto selecione o ticket e a data de vencimento
        </Typography>

        <Flex direction="column" gap={12}>
          <Flex direction="column" gap={4}>
            <Typography type="bodySmall" weight={600}>
              Ticket ID
            </Typography>
            <div style={{ width: '100%' }}>
              <Select
                placeholder="Selecione o ticket"
                loading={isLoading}
                options={ticketOptions}
                value={ticketId ? [ticketId] : ticketOptions[0]?.value ? [ticketOptions[0].value] : undefined}
                onChange={(value) => onTicketChange(value?.[0] as string)}
                style={{ width: '100%' }}
              />
            </div>
            {hasLoaded && !isLoading && rawTickets?.length === 0 && (
              <Typography type="bodyXSmall" color="element.secondary">
                Nenhum ticket de cobrança pendente encontrado para este contrato.
              </Typography>
            )}
          </Flex>

          <Flex direction="column" gap={4}>
            <Typography type="bodySmall" weight={600}>
              Conversation ID Twilio
            </Typography>
            <Input
              placeholder="<Conversation ID Twilio>"
              value={conversationId}
              disabled
              onChange={(value: any) => onConversationChange(value)}
            />
          </Flex>

          <Flex direction="column" gap={4}>
            <Typography type="bodySmall" weight={600}>
              Escolha a data de vencimento
            </Typography>
            <AntdDatePicker
              placeholder="Selecione a data"
              value={dueDate ? moment(dueDate, 'YYYY-MM-DD') : undefined}
              onChange={(momentVal: moment.Moment | null) => onDueDateChange(momentVal?.format('YYYY-MM-DD') || '')}
              disabledDate={(current) => current && current <= moment().startOf('day')}
              style={{ width: '70%', borderRadius: 8 }}
            />
          </Flex>
        </Flex>
      </Flex>

      <Flex direction="column" bg="background.primary">
        <Divider lineType="solid" />
        <Flex justify="flex-end" pv={16}>
          <Button variant="contained" disabled={!canFinalize} loading={loading} onClick={onFinalize}>
            Finalizar
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
